/**
 * The Cloudflare Turnstile challenge, on the sign-in form.
 *
 * Renders nothing at all when Turnstile is not configured, so local development
 * and any environment without a key behaves exactly as before. That mirrors the
 * server, which does not enforce what is not configured.
 *
 * The script is loaded once per page and shared. Cloudflare's snippet is
 * ordinarily a `<script>` in the document head with a magic `cf-turnstile`
 * class, which does not fit a single-page app: the element it looks for does not
 * exist at parse time. So the script is injected on demand and the widget is
 * rendered explicitly against a container this component owns.
 *
 * The CSP already permits challenges.cloudflare.com for both script-src and
 * frame-src, which is why no header change was needed here.
 */

import { useEffect, useRef, useState } from 'react';

/** The subset of Cloudflare's API this uses. */
interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => string;
  reset: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Resolves once Cloudflare's script is available, loading it if needed. One
 *  promise for the page, so two widgets cannot race two script tags. */
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }

    const el = document.createElement('script');
    el.id = SCRIPT_ID;
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.addEventListener('load', () => resolve());
    el.addEventListener('error', () => reject(new Error('turnstile script failed')));
    document.head.appendChild(el);
  });

  return scriptPromise;
}

export default function TurnstileWidget({
  siteKey,
  onToken,
  onUnavailable,
}: {
  /** Null when Turnstile is not configured, which renders nothing. */
  siteKey: string | null;
  /** Called with a fresh token, and with null when one expires or errors, so
   *  the form can stop offering a token it no longer has. */
  onToken: (token: string | null) => void;
  /** Told when the challenge cannot be drawn at all, so the form can explain
   *  itself rather than offering a button the server will refuse. */
  onUnavailable?: () => void;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!siteKey || !holder.current) return;

    let widgetId: string | undefined;
    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || !holder.current || !window.turnstile) return;
        widgetId = window.turnstile.render(holder.current, {
          sitekey: siteKey,
          theme: 'light',
          callback: (token) => onToken(token),
          /* A token is single use and short lived. Clearing it means the form
             asks for another rather than sending one the server will refuse. */
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => {
        if (cancelled) return;
        /*
         * A blocked script means no token, and no token IS a refusal server
         * side — it has to be, or the check is bypassed by simply not sending
         * the header. The server's fail-open rule covers Cloudflare being
         * unreachable from OUR side, not the widget failing in the browser.
         *
         * So this is a dead end for link sign-in, and the form says so
         * instead of offering a button that will be refused.
         */
        setFailed(true);
        onToken(null);
        onUnavailable?.();
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
    };
    // onToken is stable in practice; re-rendering the widget on every keystroke
    // would reset the challenge mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;

  return (
    <div className="turnstile">
      <div ref={holder} />
      {failed && (
        <p className="fdesc" style={{ marginTop: 6 }}>
          The security challenge could not load, usually a browser extension or a network
          blocking it. Reload the page, or sign in with Google if you have a Front Porch account.
        </p>
      )}
    </div>
  );
}
